import { useState, useEffect } from 'react';
import Table from './Table';
import { createUser, deleteUser, editUser, listAllUsers, resendInvite } from '../../service/UserService';
import { User } from '../../models/User';
import { License } from '../../models/License';
import { listAllLicenses } from '../../service/LicenseService';
import { UserCreateRequest } from '../../models/UserCreateRequest';
import { UserEditRequest } from '../../models/UserEditRequest';
import { verifyPassword } from '../../service/AuthService';
import { useAuthContext } from '../../contexts/AuthContext';
import axios from 'axios';

const btn = {
    primary:   'gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition text-sm font-medium',
    secondary: 'border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition text-sm',
    danger:    'bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition text-sm font-medium',
};
const input  = 'w-full px-4 py-2 border border-gray-300 rounded-lg text-sm transition';
const modal  = 'bg-white rounded-2xl shadow-xl p-6 w-full max-w-md';
const overlay = 'fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50';

const UsersSection = () => {
    const { user: currentUser } = useAuthContext();
    const isGod = currentUser?.profiles?.includes('GOD') ?? false;

    const [users, setUsers] = useState([] as User[]);
    const [isLoading, setIsLoading] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [loadingEdit, setLoadingEdit] = useState(false);
    const [loadingCreate, setLoadingCreate] = useState(false);
    const [errorModal, setErrorModal] = useState({ show: false, message: '' });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [successModal, setSuccessModal] = useState({ show: false, link: '' });
    const [licenses, setLicenses] = useState([] as License[]);
    const [showGodConfirmModal, setShowGodConfirmModal] = useState(false);
    const [godConfirmPassword, setGodConfirmPassword] = useState('');
    const [godConfirmError, setGodConfirmError] = useState('');
    const [loadingGodConfirm, setLoadingGodConfirm] = useState(false);
    const [showResendModal, setShowResendModal] = useState(false);
    const [resendUser, setResendUser] = useState<User | null>(null);
    const [loadingResend, setLoadingResend] = useState(false);
    const [resendSuccessModal, setResendSuccessModal] = useState(false);

    const [newUser, setNewUser] = useState({
        email: '',
        displayName: '',
        profile: 'USER',
        licenseHash: '',
    });

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            setUsers(await listAllUsers());
        } catch (error) {
            console.error('Failed to fetch users:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchLicenses = async () => {
        try {
            setLicenses(await listAllLicenses());
        } catch (error) {
            console.error('Failed to fetch licenses:', error);
        }
    };

    const handleCreateUser = async () => {
        if (!newUser.email || !newUser.displayName || !newUser.licenseHash) {
            setErrorModal({ show: true, message: 'All fields are required.' });
            return;
        }
        if (!/\S+@\S+\.\S+/.test(newUser.email)) {
            setErrorModal({ show: true, message: 'Please enter a valid email address.' });
            return;
        }
        setLoadingCreate(true);
        try {
            await createUser({
                email: newUser.email,
                displayName: newUser.displayName,
                profiles: [newUser.profile],
                licenseHash: newUser.licenseHash,
            } as UserCreateRequest);
            setShowCreateModal(false);
            setSuccessModal({ show: true, link: '' });
            fetchUsers();
        } catch (error) {
            console.error('Failed to create user:', error);
            let errorMessage = 'An unexpected error occurred.';
            if (axios.isAxiosError(error)) errorMessage = error.response?.data?.detail || errorMessage;
            setErrorModal({ show: true, message: errorMessage });
        } finally {
            setLoadingCreate(false);
        }
    };

    const handleEditUser = async () => {
        if (!selectedUser) return;
        if (selectedUser.profiles.includes('GOD')) {
            setGodConfirmPassword('');
            setGodConfirmError('');
            setShowGodConfirmModal(true);
            return;
        }
        await submitEditUser();
    };

    const submitEditUser = async () => {
        if (!selectedUser) return;
        setLoadingEdit(true);
        try {
            await editUser(selectedUser.userId, {
                displayName: selectedUser.displayName,
                profiles: selectedUser.profiles,
                disabled: selectedUser.disabled,
            } as UserEditRequest);
            setShowEditModal(false);
            fetchUsers();
        } catch (error) {
            console.error('Failed to edit user:', error);
            let errorMessage = 'An unexpected error occurred.';
            if (axios.isAxiosError(error)) errorMessage = error.response?.data?.detail || errorMessage;
            setErrorModal({ show: true, message: errorMessage });
        } finally {
            setLoadingEdit(false);
        }
    };

    const handleGodConfirm = async () => {
        if (!godConfirmPassword) {
            setGodConfirmError('Please enter your password.');
            return;
        }
        setLoadingGodConfirm(true);
        setGodConfirmError('');
        try {
            await verifyPassword(godConfirmPassword);
            setShowGodConfirmModal(false);
            await submitEditUser();
        } catch (error: any) {
            setGodConfirmError(error.message || 'Password is incorrect.');
        } finally {
            setLoadingGodConfirm(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) return;
        setLoadingDelete(true);
        try {
            await deleteUser(selectedUser.userId);
            setShowDeleteModal(false);
            fetchUsers();
        } catch (error) {
            console.error('Failed to delete user:', error);
            let errorMessage = 'An unexpected error occurred.';
            if (axios.isAxiosError(error)) errorMessage = error.response?.data?.detail || errorMessage;
            setErrorModal({ show: true, message: errorMessage });
        } finally {
            setLoadingDelete(false);
        }
    };

    const handleResendInvite = async () => {
        if (!resendUser) return;
        setLoadingResend(true);
        try {
            await resendInvite(resendUser.userId);
            setShowResendModal(false);
            setResendSuccessModal(true);
            fetchUsers();
        } catch (error) {
            console.error('Failed to resend invite:', error);
            let errorMessage = 'An unexpected error occurred.';
            if (axios.isAxiosError(error)) errorMessage = error.response?.data?.detail || errorMessage;
            setErrorModal({ show: true, message: errorMessage });
        } finally {
            setLoadingResend(false);
        }
    };

    const formatTimeRemaining = (expiresAt: string): string => {
        const diff = new Date(expiresAt).getTime() - Date.now();
        if (diff <= 0) return 'expired';
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        if (hours > 0) return `${hours}h ${minutes}m remaining`;
        return `${minutes}m remaining`;
    };

    const columns = [
        { key: 'displayName', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'profiles', label: 'Profiles', render: (profiles: string[]) => profiles.join(', ') },
        {
            key: 'disabled',
            label: 'Status',
            render: (disabled: boolean, user: User) => {
                if (disabled) {
                    return <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-700">Disabled</span>;
                }
                if (user.pendingPasswordSetup && user.pendingSetupTokenExpiresAt) {
                    const tooltip = `User has not set their password yet. Link ${formatTimeRemaining(user.pendingSetupTokenExpiresAt)}.`;
                    return (
                        <span
                            className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 cursor-help"
                            title={tooltip}
                        >
                            Pending
                        </span>
                    );
                }
                return <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-700">Active</span>;
            },
        },
        { key: 'licenseName', label: 'License Name' },
    ];

    const actions = [
        {
            label: 'Edit',
            className: 'bg-blue-50 text-blue-700 px-3 py-1 rounded-lg hover:bg-blue-100 transition text-sm',
            onClick: (user: User) => { setSelectedUser({ ...user }); setShowEditModal(true); },
        },
        {
            label: 'Resend Invite',
            className: 'bg-yellow-50 text-yellow-700 px-3 py-1 rounded-lg hover:bg-yellow-100 transition text-sm',
            condition: (user: User) => !!user.pendingPasswordSetup,
            onClick: (user: User) => { setResendUser(user); setShowResendModal(true); },
        },
        {
            label: 'Delete',
            className: 'bg-red-50 text-red-600 px-3 py-1 rounded-lg hover:bg-red-100 transition text-sm',
            onClick: (user: User) => { setSelectedUser(user); setShowDeleteModal(true); },
        },
    ];

    const openCreateModal = () => {
        setNewUser({ email: '', displayName: '', profile: 'USER', licenseHash: '' });
        setShowCreateModal(true);
    };

    useEffect(() => {
        fetchUsers();
        fetchLicenses();
    }, []);

    return (
        <section id="usersSection" className="dashboard-section">
            <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-800">Manage Users</h2>
                    <button className={`${btn.primary} flex items-center`} onClick={openCreateModal}>
                        <i className="fas fa-plus mr-2"></i> Add User
                    </button>
                </div>
                {isLoading ? <div className="text-center py-8 text-gray-400">Loading...</div> : <Table columns={columns} data={users} actions={actions} />}
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className={overlay}>
                    <div className={modal}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-5">Create User</h3>
                        <form className="space-y-4">
                            {[
                                { label: 'Email', type: 'email', key: 'email', auto: 'off' },
                                { label: 'Display Name', type: 'text', key: 'displayName', auto: 'off' },
                            ].map(({ label, type, key, auto }) => (
                                <div key={key}>
                                    <label className="block text-gray-700 font-medium mb-1 text-sm">{label}</label>
                                    <input type={type} className={input} autoComplete={auto}
                                        value={(newUser as any)[key]}
                                        onChange={e => setNewUser({ ...newUser, [key]: e.target.value })} />
                                </div>
                            ))}
                            <div>
                                <label className="block text-gray-700 font-medium mb-1 text-sm">Profile</label>
                                <select className={input} value={newUser.profile}
                                    onChange={e => setNewUser({ ...newUser, profile: e.target.value })}>
                                    <option value="ADMIN">ADMIN</option>
                                    <option value="USER">USER</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-gray-700 font-medium mb-1 text-sm">License</label>
                                <select className={input} value={newUser.licenseHash}
                                    onChange={e => setNewUser({ ...newUser, licenseHash: e.target.value })}>
                                    <option value="">Select License</option>
                                    {licenses.map(l => <option key={l.licenseHash} value={l.licenseHash}>{l.name}</option>)}
                                </select>
                            </div>
                            <div className="flex justify-between pt-2">
                                <button type="button" className={btn.secondary} onClick={() => setShowCreateModal(false)} disabled={loadingCreate}>Cancel</button>
                                <button type="button" className={btn.primary} onClick={handleCreateUser} disabled={loadingCreate}>
                                    {loadingCreate ? 'Creating...' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedUser && (
                <div className={overlay}>
                    <div className={modal}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-5">Edit User</h3>
                        <form className="space-y-4">
                            <div>
                                <label className="block text-gray-700 font-medium mb-1 text-sm">Display Name</label>
                                <input type="text" className={input} value={selectedUser.displayName}
                                    onChange={e => setSelectedUser({ ...selectedUser, displayName: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-gray-700 font-medium mb-1 text-sm">Profile</label>
                                <select className={input} value={selectedUser.profiles[0]}
                                    onChange={e => setSelectedUser({ ...selectedUser, profiles: [e.target.value] })}>
                                    {isGod && <option value="GOD">GOD</option>}
                                    <option value="ADMIN">ADMIN</option>
                                    <option value="USER">USER</option>
                                </select>
                            </div>
                            <div className="flex items-center">
                                <input type="checkbox" id="disabled" className="mr-2" checked={selectedUser.disabled}
                                    onChange={e => setSelectedUser({ ...selectedUser, disabled: e.target.checked })} />
                                <label htmlFor="disabled" className="text-gray-700 font-medium text-sm">Disabled</label>
                            </div>
                            <div className="flex justify-between pt-2">
                                <button type="button" className={btn.secondary} onClick={() => setShowEditModal(false)} disabled={loadingEdit}>Cancel</button>
                                <button type="button" className={btn.primary} onClick={handleEditUser} disabled={loadingEdit}>
                                    {loadingEdit ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {showDeleteModal && selectedUser && (
                <div className={overlay}>
                    <div className={`${modal} text-center`}>
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-trash text-red-500"></i>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete User</h3>
                        <p className="text-gray-600 text-sm mb-6">
                            Are you sure you want to delete <strong>{selectedUser.displayName}</strong>? This action cannot be undone.
                        </p>
                        <div className="flex justify-between gap-3">
                            <button type="button" className={`${btn.secondary} flex-1`} onClick={() => setShowDeleteModal(false)} disabled={loadingDelete}>Cancel</button>
                            <button type="button" className={`${btn.danger} flex-1`} onClick={handleDeleteUser} disabled={loadingDelete}>
                                {loadingDelete ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading overlay */}
            {(loadingEdit || loadingDelete || loadingCreate) && (
                <div className={overlay}>
                    <div className="loader border-t-4 border-white rounded-full w-12 h-12 animate-spin"></div>
                </div>
            )}

            {/* Success Modal */}
            {successModal.show && (
                <div className={overlay}>
                    <div className={`${modal} text-center`}>
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-envelope-open-text text-green-500"></i>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">User Created</h3>
                        <p className="text-gray-600 text-sm mb-5">
                            An email has been sent to the user with a link to set their password. The link expires in 24 hours.
                        </p>
                        <button className={btn.primary} onClick={() => setSuccessModal({ show: false, link: '' })}>Close</button>
                    </div>
                </div>
            )}

            {/* Resend Invite Modal */}
            {showResendModal && resendUser && (
                <div className={overlay}>
                    <div className={`${modal} text-center`}>
                        <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-envelope text-yellow-500"></i>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Resend Invite</h3>
                        <p className="text-gray-600 text-sm mb-6">
                            This will cancel the existing invite link for <strong>{resendUser.displayName || resendUser.email}</strong> and send a new one valid for 24 hours. Continue?
                        </p>
                        <div className="flex justify-between gap-3">
                            <button type="button" className={`${btn.secondary} flex-1`} onClick={() => setShowResendModal(false)} disabled={loadingResend}>Cancel</button>
                            <button type="button" className={`${btn.primary} flex-1`} onClick={handleResendInvite} disabled={loadingResend}>
                                {loadingResend ? 'Sending...' : 'Send'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Resend Invite Success Modal */}
            {resendSuccessModal && (
                <div className={overlay}>
                    <div className={`${modal} text-center`}>
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-envelope-open-text text-green-500"></i>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Invite Sent</h3>
                        <p className="text-gray-600 text-sm mb-5">
                            A new invite email has been sent. The previous link has been cancelled. The new link expires in 24 hours.
                        </p>
                        <button className={btn.primary} onClick={() => setResendSuccessModal(false)}>Close</button>
                    </div>
                </div>
            )}

            {/* GOD Confirmation Modal */}
            {showGodConfirmModal && (
                <div className={overlay}>
                    <div className={modal}>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5">
                            <div className="flex items-start gap-3">
                                <i className="fas fa-exclamation-triangle text-red-500 mt-0.5"></i>
                                <div>
                                    <p className="text-red-800 font-semibold text-sm">GOD Profile Warning</p>
                                    <p className="text-red-700 text-sm mt-1">
                                        You are about to grant GOD-level access. This profile has full unrestricted permissions.
                                        Please confirm your identity to proceed.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-gray-700 font-medium mb-1 text-sm">Your Password</label>
                                <input
                                    type="password"
                                    className={input}
                                    placeholder="Enter your current password"
                                    value={godConfirmPassword}
                                    onChange={e => { setGodConfirmPassword(e.target.value); setGodConfirmError(''); }}
                                    autoComplete="current-password"
                                />
                                {godConfirmError && <p className="text-red-500 text-xs mt-1">{godConfirmError}</p>}
                            </div>
                            <div className="flex justify-between pt-2">
                                <button type="button" className={btn.secondary} onClick={() => setShowGodConfirmModal(false)} disabled={loadingGodConfirm}>Cancel</button>
                                <button type="button" className={btn.danger} onClick={handleGodConfirm} disabled={loadingGodConfirm}>
                                    {loadingGodConfirm ? 'Verifying...' : 'Confirm'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Modal */}
            {errorModal.show && (
                <div className={overlay}>
                    <div className={`${modal} text-center`}>
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-exclamation text-red-500"></i>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Error</h3>
                        <p className="text-gray-600 text-sm mb-5">{errorModal.message}</p>
                        <button className={btn.primary} onClick={() => setErrorModal({ show: false, message: '' })}>Close</button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default UsersSection;
